<template>
  <div>
    <v-card class="sf-card"
            flat>
      <div class="sf-card__header"
           v-if="!hideHeader">
        <h2 class="sf-card__title">{{ $t('orders') }}</h2>
        <div class="sf-card__tools">
          <v-text-field class="sf-search sf-orders__search noprint"
                        dense
                        flat
                        hide-details
                        prepend-inner-icon="ti-search"
                        solo
                        v-bind:placeholder="$t('search')"
                        v-model="search" />
        </div>
      </div>
      <v-progress-linear color="accent"
                         indeterminate
                         v-bind:active="loading" />
      <!-- Smartphone: gruppierte Liste statt Tabelle -->
      <div class="sf-order-list"
           v-if="$vuetify.breakpoint.xsOnly">
        <div class="sf-order-list__empty"
             v-if="mobileGroups.length === 0">
          {{ orders.length === 0 ? $t('thereAreCurrentlyNoOrders') : $t('noOrdersWereFound') }}
        </div>
        <section class="sf-order-list__group"
                 v-bind:key="group.key"
                 v-for="group in mobileGroups">
          <div class="sf-order-list__group-head">
            <span class="sf-order-list__group-title">{{ group.title }}</span>
            <v-chip class="sf-pill sf-pill--neutral"
                    x-small>{{ group.items.length }}</v-chip>
          </div>
          <div class="sf-order-list__item"
               v-bind:key="`${item.supplierId}-${item.productId}-${item.readableDeliveryPeriod}-${index}`"
               v-for="(item, index) in group.items">
            <div class="sf-order-list__main">
              <div class="sf-order-list__title">{{ item.productTitle }}</div>
              <div class="sf-order-list__meta">
                <v-chip class="sf-pill sf-pill--primary"
                        x-small>{{ item.readableDeliveryPeriod }}</v-chip>
                <span class="sf-tabular">{{ item.quantity }} × {{ item.packaging }}</span>
                <span class="accent--text font-weight-medium"
                      v-if="item.flag">{{ item.flag }}</span>
              </div>
            </div>
            <div class="sf-order-list__side">
              <template v-if="!hideConditions">
                <div class="sf-order-list__price">{{ formatPrice(item.price) }}</div>
                <div class="sf-order-list__discount accent--text"
                     v-if="item.discount">{{ item.discount }}</div>
              </template>
              <template v-if="!hideEdit">
                <v-btn color="primary"
                       icon
                       small
                       v-bind:title="$t('edit')"
                       v-if="isAdmin"
                       v-on:click="$emit('go-to-supplier', item.supplierId )">
                  <v-icon small>ti-pencil</v-icon>
                </v-btn>
                <v-btn color="primary"
                       icon
                       small
                       v-bind:title="$t('edit')"
                       v-if="isLive"
                       v-bind:to="{ name: 'supplier', params: { id: item.supplierId } }">
                  <v-icon small>ti-pencil</v-icon>
                </v-btn>
              </template>
            </div>
          </div>
        </section>
      </div>
      <v-data-table v-else
                    v-bind:group-by="showCustomers ? 'customerId' : 'supplierId'"
                    v-bind:custom-filter="filter"
                    v-bind:headers="hideConditions ? headersWithoutConditions : headers"
                    v-bind:items="orders"
                    v-bind:items-per-page="itemsPerPage"
                    v-bind:no-data-text="$t('thereAreCurrentlyNoOrders')"
                    v-bind:no-results-text="$t('noOrdersWereFound')"
                    v-bind:search="filterText != null ? filterText : search"
                    v-bind:sort-by="['supplierId', 'productId', 'readableDeliveryPeriod']">
        <template v-slot:item.actions="{ item }" v-if="!hideEdit">
          <v-btn color="primary"
                 v-if="isAdmin"
                 icon
                 small
                 v-bind:title="$t('edit')"
                 v-on:click="$emit('go-to-supplier', item.supplierId )">
            <v-icon small>
              ti-pencil
            </v-icon>
          </v-btn>

          <v-btn color="primary"
                 v-if="isLive"
                 icon
                 small
                 v-bind:title="$t('edit')"
                 v-bind:to="{ name: 'supplier', params: { id: item.supplierId } }">
            <v-icon small>
              ti-pencil
            </v-icon>
          </v-btn>
        </template>
        <template v-slot:item.price="{ item }">
          {{ formatPrice(item.price) }}
        </template>
        <template v-slot:group.header="{ headers, isOpen, items, toggle }">
          <td class="sf-orders__group"
              v-bind:colspan="headers.length">
            <v-btn class="mr-1"
                   icon
                   small
                   v-on:click="toggle">
              <v-icon>{{ isOpen ? 'ti-chevron-down' : 'ti-chevron-right' }}</v-icon>
            </v-btn>
            <strong v-if="showCustomers">{{ items[0].customerDescription }}</strong>
            <strong v-else>{{ items[0].supplierFriendlyName }}</strong>
          </td>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
  export default {
    data: () => ({
      search: null
    }),
    computed: {
      mobileGroups() {
        const term = this.filterText != null ? this.filterText : this.search
        const fields = [ 'readableDeliveryPeriod', 'productTitle', 'productId', 'supplierFriendlyName', 'customerDescription', 'flag', 'packaging' ]

        let items = [ ...this.orders ]

        if (term != null && term.length !== 0) {
          items = items.filter(o => fields.some(f => this.filter(o[f], term, o) === true))
        }

        const compare = (a, b) => `${a}`.localeCompare(`${b}`, undefined, { numeric: true })

        items.sort((a, b) => compare(a.supplierId, b.supplierId) || compare(a.productId, b.productId) || compare(a.readableDeliveryPeriod, b.readableDeliveryPeriod))

        const groupKey = this.showCustomers ? 'customerId' : 'supplierId'
        const groupTitle = this.showCustomers ? 'customerDescription' : 'supplierFriendlyName'

        return items.reduce((groups, item) => {
          let group = groups.find(g => g.key === item[groupKey])

          if (group == null) {
            group = { key: item[groupKey], title: item[groupTitle], items: [] }
            groups.push(group)
          }

          group.items.push(item)

          return groups
        }, [])
      },
      headers() {
        return [
          {
            text: this.$t('period'),
            value: 'readableDeliveryPeriod'
          },
          {
            text: this.$t('product'),
            value: 'productTitle'
          },
          {
            text: this.$t('price'),
            value: 'price'
          },
          {
            text: this.$t('discount'),
            value: 'discount'
          },
          {
            text: this.$t('quantity'),
            value: 'quantity'
          },
          {
            text: this.$t('unit'),
            value: 'packaging'
          },
          {
            text: '',
            value: 'supplierId'
          },
          {
            text: 'Kennzeichen',
            value: 'flag'
          },
          {
            sortable: false,
            text: '',
            value: 'actions'
          }
        ]
      },
      headersWithoutConditions() {
        return [
          {
            text: this.$t('period'),
            value: 'readableDeliveryPeriod'
          },
          {
            text: this.$t('product'),
            value: 'productTitle'
          },
          {
            text: this.$t('quantity'),
            value: 'quantity'
          },
          {
            text: this.$t('unit'),
            value: 'packaging'
          },
          {
            text: 'Kennzeichen',
            value: 'flag'
          },
          {
            text: '',
            value: 'supplierId'
          },
          {
            sortable: false,
            text: '',
            value: 'actions'
          }
        ]
      }
    },
    methods: {
      filter(value, search, item) {
        if (value == null) {
          return null
        }

        if (search == null) {
          return null
        }

        return value.toString().toLocaleUpperCase().indexOf(search.toString().toLocaleUpperCase()) !== -1
      },
      formatPrice(price) {
        if (price == null || price <= 0) {
          return 'n. v.'
        }

        return `${price.toLocaleString(this.$i18n.locale, {
          style: 'currency',
          currency: 'EUR'
        })}*`
      },
      print() {
        this.itemsPerPage = null
      }
    },
    name: 'OrderSummaryCard',
    props: {
      filterText: String,
      hideHeader: Boolean,
      hideConditions: Boolean,
      hideEdit: Boolean,
      itemsPerPage: Number,
      orders: Array,
      loading: Boolean,
      isLive: Boolean,
      isAdmin: Boolean,
      showCustomers: Boolean
    }
  }
</script>

<style scoped>
  .sf-orders__search {
    flex: 1 1 200px;
    max-width: 320px;
  }

  .sf-orders__group {
    font-size: .9375rem;
  }

  .sf-order-list__empty {
    color: var(--sf-text-secondary);
    padding: 24px 16px;
    text-align: center;
  }

  .sf-order-list__group-head {
    align-items: center;
    background: var(--sf-primary-soft);
    color: var(--sf-primary);
    display: flex;
    font-weight: 600;
    gap: 8px;
    padding: 10px 16px;
  }

  .sf-order-list__group-title {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .sf-order-list__item {
    align-items: flex-start;
    border-bottom: 1px solid var(--sf-border);
    display: flex;
    gap: 12px;
    padding: 12px 16px;
  }

  .sf-order-list__item:last-child {
    border-bottom: 0;
  }

  .sf-order-list__main {
    flex: 1 1 auto;
    min-width: 0;
  }

  .sf-order-list__title {
    font-size: .9375rem;
    font-weight: 500;
    line-height: 1.4;
  }

  .sf-order-list__meta {
    align-items: center;
    color: var(--sf-text-secondary);
    display: flex;
    flex-wrap: wrap;
    font-size: .8125rem;
    gap: 6px 10px;
    margin-top: 6px;
  }

  .sf-order-list__side {
    align-items: flex-end;
    display: flex;
    flex: 0 0 auto;
    flex-direction: column;
    gap: 2px;
    text-align: right;
  }

  .sf-order-list__price {
    font-size: .9375rem;
    font-variant-numeric: tabular-nums;
    font-weight: 600;
  }

  .sf-order-list__discount {
    font-size: .8125rem;
  }
</style>

<i18n>
  {
    "de": {
      "discount": "Kondition",
      "noOrdersWereFound": "Es wurden keine Bestellungen gefunden",
      "orders": "Bestellungen",
      "period": "Zeitraum",
      "price": "Preis*",
      "product": "Artikel",
      "quantity": "Menge",
      "thereAreCurrentlyNoOrders": "Es sind noch keine Bestellungen vorhanden",
      "unit": "Einheit",
      "search": "Suchen",
      "edit": "Bearbeiten"
    },
    "en": {
      "discount": "Discount",
      "noOrdersWereFound": "No orders were found",
      "orders": "Orders",
      "period": "Period",
      "price": "Price",
      "product": "Product",
      "quantity": "Quantity",
      "thereAreCurrentlyNoOrders": "There are currently no orders",
      "unit": "Unit",
      "search": "Search",
      "edit": "Edit"
    }
  }
</i18n>